/**
 * Shared internationalization types used across multiple modules
 * to avoid circular dependencies.
 */

/**
 * System country codes used for backend API communication
 */
export enum SystemCountry {
  PL = 'pl',
  EN = 'en',
  DE = 'de',
  LT = 'lt',
  FR = 'fr',
  ES = 'es',
  IT = 'it',
  PT = 'pt',
  NL = 'nl',
  CS = 'cs',
  SK = 'sk',
  HU = 'hu',
  RO = 'ro',
  BG = 'bg',
  HR = 'hr',
  SL = 'sl',
  EL = 'el',
  ET = 'et',
  LV = 'lv',
  FI = 'fi',
  SV = 'sv',
  DA = 'da',
  MT = 'mt',
}

/**
 * Translation map for different system countries.
 * Uses index signature for compatibility with API response data.
 * Expected keys are 'pl' and 'en' matching SystemCountry values.
 */
export type Translations = {
  [key: string]: string;
};

/**
 * Label interface with internationalization support.
 * This is the canonical Label type - use this throughout the codebase.
 */
export interface Label {
  i18n: Translations;
}

/**
 * An unfilled server-side template placeholder: `{0}`, `{1}`, `{date}`.
 *
 * The backend builds some labels from templates and substitutes the values
 * before sending them. When a template gets the placeholder syntax wrong the
 * substitution silently does nothing, and the raw marker travels all the way
 * to the customer — DOD-646 put "Jusquau {0}" on a production price callout in
 * French while the other five languages read correctly.
 */
const UNFILLED_PLACEHOLDER = /\{[A-Za-z0-9_]*\}/;

/** Whether this string still carries a placeholder nobody filled in. */
export function hasUnfilledPlaceholder(text: string | null | undefined): boolean {
  return !!text && UNFILLED_PLACEHOLDER.test(text);
}

/**
 * The best readable text for a locale, skipping anything still holding a
 * placeholder.
 *
 * Order: the reader's language, then English, then nothing. English is already
 * the fallback for a locale the label does not carry; this extends the same
 * rule to a locale it carries *broken*, because a correct date in the wrong
 * language beats a raw `{0}` in the right one, and an empty callout beats
 * both when nothing usable is left.
 *
 * `lang` is the short code (`fr`), not the app language (`fr-FR`).
 */
export function readableLabel(
  label: Label | null | undefined,
  lang: string,
): string {
  if (!label?.i18n) return '';
  for (const candidate of [label.i18n[lang], label.i18n['en']]) {
    if (candidate && !hasUnfilledPlaceholder(candidate)) return candidate;
  }
  return '';
}

